
import { defineComponent, ref, reactive, watch, nextTick, onMounted, onBeforeUnmount, getCurrentInstance, onBeforeMount } from 'vue'
import { mapState, useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import COVERAPP from '@/utils/COVERAPP'
import ResourceTracker from '@/utils/threeResource'

export default defineComponent({
    computed: {
        ...mapState(['activityData'])
    },
    emits: ['load'],
    setup (props, {expose, emit}) {
        const $store = useStore()
        const _this = getCurrentInstance() as any
        const COVER = _this.proxy.$common
        const activityData = $store.state.activityData
        const isLoaded = ref<boolean>(false)
        let resMgr = new ResourceTracker()
        const resTrack = resMgr.track.bind(resMgr)
        const isWap = COVERAPP.Attribute.isMb()

        
        // 创建渲染器
        let renderer: any = new THREE.WebGLRenderer({preserveDrawingBuffer: true, antialias: true})
        renderer.setPixelRatio(window.devicePixelRatio)
        renderer.setSize(window.innerWidth, window.innerHeight)

        // 创建场景
        let scene: any = new THREE.Scene()
        
        // 创建相机
        let camera: any = new THREE.PerspectiveCamera(isWap ? 80 : 40, window.innerWidth / window.innerHeight, 1, 1000)
        camera.position.set(0, 0, 0.01)
        scene.add(camera)

        // 镜头控制器
        const controls = new OrbitControls(camera, renderer.domElement)
        controls.target.set(0, 0, 0)
        controls.rotateSpeed = -.5
        // 禁止缩放
        controls.enableZoom = false
        // 自动巡游
        // controls.autoRotate = true
        // controls.autoRotateSpeed = .1
        controls.addEventListener('change', () => {
            renderer.render(scene, camera)
        })

        const createPanoramaMaterial = (imageUrls, segments: number, w: number, h: number) => {
            return new Promise<any>((resolve, reject) => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
                canvas.width = w
                canvas.height = h
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                // 分段加载图片
                const images: any[] = []
                let imgCount = 0
                for (let i = 0; i < segments; i++) {
                    const image = new Image()
                    image.src = imageUrls[i]
                    image.crossOrigin = 'Anonymous' 
                    images.push(image)
                    image.onload = () => {
                        imgCount++
                        const size = Math.sqrt(segments)
                        const w = canvas.width / size
                        const h = canvas.height / size
                        ctx.drawImage(image, 0, 0, w, h, i % size * w, Math.floor(i / size) * h, w, h)
                        if (imgCount === segments) {
                            resolve(canvas)
                        }   
                    }
                }
            })
        }

        let geometry: any = null
        const createPc = () => {
            geometry = new THREE.SphereGeometry(500, 60, 40)
            geometry.scale(1, 1, -1)
            // 定义全景图分段数量
            const segments = 100
            // 全景图图片URL数组
            const imageUrls: string[] = []
            for (let i = 1; i <= segments; i++) {
                imageUrls.push(`https://yunyingcdn.thecover.cn/ssccloudtour/img/images/pic_${i < 10 ? '0' + i : i}.jpg`)  
            }
            // 创建球体材质
            createPanoramaMaterial(imageUrls, segments, 10560, 5280).then((canvas) => {
                const texture = resTrack(new THREE.Texture(canvas))
                texture.needsUpdate = true
                texture.colorSpace = THREE.SRGBColorSpace
                texture.minFilter = THREE.LinearFilter
                texture.magFilter = THREE.LinearFilter 
                const sphereMaterial = resTrack(new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide,}))
                const sphere = resTrack(new THREE.Mesh(geometry, sphereMaterial))
                scene.add(sphere)
                renderer.render(scene, camera)
                emit('load')
            })
        }

        const createWap = () => {
            geometry = new THREE.BoxGeometry(800, 800, 800)
            const materials: any[] = []
            const position = ['px', 'nx', 'py', 'ny', 'pz', 'nz']
            let loadNum = 0
            const segments = 16
            for (let i = 0; i < position.length; i++) {
                // 全景图图片URL数组
                const imageUrls: string[] = []
                for (let j = 1; j <= segments; j++) {
                    imageUrls.push(`https://yunyingcdn.thecover.cn/ssccloudtour/img/${position[i]}/${position[i]}_${j < 10 ? '0' + j : j}.jpg`)  
                }
                createPanoramaMaterial(imageUrls, segments, 2048, 2048).then((canvas) => {
                    const texture = resTrack(new THREE.Texture(canvas))
                    texture.needsUpdate = true
                    texture.colorSpace = THREE.SRGBColorSpace
                    materials[i] = resTrack(new THREE.MeshBasicMaterial({ map: texture}))
                    loadNum++
                    if (loadNum >= 6) {
                        const skyBox = resTrack(new THREE.Mesh(geometry, materials))
                        skyBox.geometry.scale(1, 1, -1)
                        scene.add(skyBox)
                        renderer.render(scene, camera)
                        emit('load')
                    }
                })
            }
        }
        
        if (!isWap) {
            createPc()
        } else {
            camera.lookAt(new THREE.Vector3(1, 0, 0))
            controls.target.copy(new THREE.Vector3(1, 0, 0))
            createWap()
        }
        
        let container = null as any
        const resize = () => {
            nextTick(() => {
                const winWidth = window.innerWidth
                const winHeight = window.innerHeight
                camera.aspect = winWidth / winHeight
                camera.updateProjectionMatrix()
                renderer.setSize(winWidth, winHeight)
                renderer.render(scene, camera)
            })
        }

        const reset = () => {
            controls.reset()
            if (isWap) {
                controls.target.copy(new THREE.Vector3(1, 0, 0))
                camera.lookAt(new THREE.Vector3(1, 0, 0))
                renderer.render(scene, camera)
            }
        }

        const destroy = () => {
            try {
                resMgr && resMgr.dispose()
                renderer.dispose()
                renderer.forceContextLoss()
                renderer.content = null
                let gl = renderer.domElement.getContext('webgl')
                if (gl && gl.getExtension('WEBGL_lose_context')) {
                    gl.getExtension('WEBGL_lose_context').loseContext()
                }
                scene.traverse((child) => {
                    if (child.material) {
                        child.material.dispose()
                    }
                    if (child.geometry) {
                        child.geometry.dispose()
                    }
                    child = null
                })
                scene.clear()
                container.removeChild(renderer.domElement)
                scene = null
                renderer = null
                camera = null
            } catch (e) {
                console.error("Failed to destroy threejs", e)
            }
        }

        onBeforeUnmount(() => {
            destroy()
            window.removeEventListener('resize', resize)
        })

        onMounted(() => {
            container = document.querySelector('.panoramic-wrapper') as HTMLDivElement
            container.appendChild(renderer.domElement)
            window.addEventListener('resize', resize)
        })  
        
        expose({ 
            reset
        })


        return {
            
        }
    }
})
